import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Shield, Headphones, Calculator, Plus, Edit, Trash2 } from 'lucide-react';

const SYSTEM_TYPES = [
  { name: 'Customer', slug: 'customer', description: 'End users who book services', color: 'bg-green-50' },
  { name: 'Provider', slug: 'provider', description: 'Service providers who fulfill bookings', color: 'bg-blue-50' },
  { name: 'Admin', slug: 'admin', description: 'Platform administrators with full access', color: 'bg-purple-50' }
];

export default function RolesTypesModal({
  open,
  onOpenChange,
  roleDefaults,
  userTypes,
  typeForm,
  setTypeForm,
  editingType,
  isSaving,
  onSaveType,
  onCreateStaffFromRole,
  onEditType,
  onDeleteType
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Roles & Types</DialogTitle>
          <DialogDescription>Configure staff roles, permissions, and custom user types</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          {/* Staff Roles */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Staff Roles</h3>
            <div className="grid gap-3">
              {roleDefaults?.roles?.map((role) => {
                const RoleIcon = role.role === 'customer_service' ? Headphones : role.role === 'accountant' ? Calculator : Shield;
                const iconBg = role.role === 'customer_service' ? 'bg-teal-100' : role.role === 'accountant' ? 'bg-emerald-100' : 'bg-indigo-100';
                const iconColor = role.role === 'customer_service' ? 'text-teal-500' : role.role === 'accountant' ? 'text-emerald-500' : 'text-indigo-500';
                return (
                  <div key={role.role} className="p-4 border border-stone-200 rounded-xl" data-testid={`role-card-${role.role}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
                          <RoleIcon className={`w-5 h-5 ${iconColor}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{role.label}</p>
                          <p className="text-xs text-slate-500 max-w-sm">{role.description}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-slate-800 hover:bg-slate-700"
                        onClick={() => onCreateStaffFromRole(role)}
                        data-testid={`create-${role.role}-btn`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Create
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(role.default_permissions || {}).map(([mod, perms]) => (
                        <div key={mod} className="flex items-center gap-1 px-2 py-1 bg-slate-50 border rounded text-xs" data-testid={`role-perm-${role.role}-${mod}`}>
                          <span className="font-medium capitalize">{mod.replace(/_/g, ' ')}</span>
                          <span className="text-slate-300">|</span>
                          {perms.can_view && <span className="text-green-600 font-bold" title="View">V</span>}
                          {perms.can_edit && <span className="text-blue-600 font-bold" title="Edit">E</span>}
                          {perms.can_delete && <span className="text-red-600 font-bold" title="Delete">D</span>}
                        </div>
                      ))}
                      {Object.keys(role.default_permissions || {}).length === 0 && (
                        <span className="text-xs text-slate-400 italic">Customizable permissions - configure when creating</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {!roleDefaults && (
                <div className="p-6 text-center text-slate-400 text-sm">Loading role configurations...</div>
              )}
            </div>
          </div>

          {/* System User Types */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-slate-900 mb-3">System User Types</h3>
            <div className="grid gap-3">
              {SYSTEM_TYPES.map((type) => (
                <div key={type.slug} className={`p-3 ${type.color} rounded-xl flex items-center justify-between`}>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{type.name}</p>
                    <p className="text-xs text-slate-500">{type.description}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-white text-slate-500 text-xs rounded-full border">System</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Types */}
          {userTypes.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="font-semibold text-slate-900 mb-3">Custom User Types</h3>
              <div className="grid gap-3">
                {userTypes.map((type) => (
                  <div key={type.id} className="p-3 border border-stone-200 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{type.name}</p>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => onEditType(type)} className="p-1.5 hover:bg-stone-100 rounded-lg">
                        <Edit className="w-3.5 h-3.5 text-slate-600" />
                      </button>
                      <button onClick={() => onDeleteType(type.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Custom Type Form */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-slate-900 mb-4">
              {editingType ? 'Edit Custom Type' : 'Add Custom User Type'}
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Type Name</Label>
                  <Input value={typeForm.name} onChange={(e) => setTypeForm(prev => ({ ...prev, name: e.target.value }))} className="h-9 mt-1" placeholder="e.g., Premium Provider" />
                </div>
                <div>
                  <Label className="text-sm">Slug</Label>
                  <Input value={typeForm.slug} onChange={(e) => setTypeForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s/g, '_') }))} className="h-9 mt-1" placeholder="e.g., premium_provider" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Input value={typeForm.description} onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))} className="h-9 mt-1" placeholder="Brief description" />
              </div>
              <Button onClick={onSaveType} disabled={isSaving || !typeForm.name || !typeForm.slug} className="w-full bg-slate-900 hover:bg-slate-800">
                {isSaving ? 'Saving...' : editingType ? 'Update Type' : 'Add User Type'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
